import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {User, UserLevel} from '../../_types/user';
import {AuthService} from '../../_services/auth.service';
import {LevelsComponent} from '../levels/levels.component';
import {BadgesComponent} from '../badges/badges.component';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {RewardsService} from '../../_services/rewards.service';
import {
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    map,
    startWith,
    Subject,
    Subscription,
    switchMap,
    takeWhile
} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Community, UserAccessLevel} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {endOfDay, endOfMonth, startOfMonth} from '../../_utils/date';
import {ThemeService} from "../../_services/theme.service";
import {CommunitiesService} from "../../_services/communities.service";
import {SocketService} from "../../_services/socket.service";
import {BadgeProgressComponent} from "../badge-progress/badge-progress.component";
import {Streak} from "../../_types/rewards";

@Component({
    selector: 'app-user-info',
    templateUrl: './user-info.component.html',
    styleUrl: './user-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserInfoComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input({required: true}) user!: Partial<User>;
    @Input() largeAvatar: boolean = false;
    @Input() hideBadges: boolean = false;
    @ViewChild('badgeProgressComponent') badgeProgressComponent: BadgeProgressComponent | undefined;
    pointsDate: string = 'allTime'
    userPoints: number = 0;
    startDate?: string;
    endDate?: string;
    isDialog: boolean = false;
    streaks: Streak[] = [];
    comments: number = 0;
    likes: number = 0;
    votes: number = 0;
    accessLevel: string | null = null;
    community: Community | null = null;
    level: UserLevel | null = null;
    private changeDate$: Subject<{ startDate?: string, endDate?: string }> = new Subject<{
        startDate?: string,
        endDate?: string
    }>();
    private subscription?: Subscription;
    private badgeSubscription?: Subscription;
    private commentSubscription?: Subscription;
    private pollVotesSubscription?: Subscription;
    private communitySubscription: Subscription;


    constructor(
        private readonly authService: AuthService,
        private readonly rewardsService: RewardsService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
        protected themeService: ThemeService,
        private socketService: SocketService,
        @Inject(MAT_DIALOG_DATA) public data: { user: Partial<User>; largeAvatar: boolean; hideBadges: boolean }
    ) {

        if (data && data.user) {
            this.isDialog = true;
            this.user = data.user;
            this.largeAvatar = data.largeAvatar != null ? data.largeAvatar : false;
            this.hideBadges = data.hideBadges != null ? data.hideBadges : false;
        }
        this.communitySubscription = this.communitiesService.getCurrentDashboardCommunity()
            .subscribe((community: Community | null): void => {
                this.community = community;
                if (!this.community?.badges_active) {
                    this.hideBadges = true;
                }
            });

    }

    // get userFullName(): string {
    //     const firstName = this.user.first_name || '';
    //     const lastName = this.user.last_name || '';
    //     return firstName.length || lastName.length ? `${firstName} ${lastName}`.trim() : this.user.username;
    // }

    get isMe(): boolean {
        return this.user?.id === this.authService.getUser()?.id;
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    ngAfterViewInit(): void {
        if (this.isMe && this.community?.id && this.user?.id) {
            this.rewardsService.getUserStreaks(this.community?.id, this.user?.id).subscribe(r => {
                this.streaks = r;
                this.cdr.detectChanges();
            }, error => {
                console.error(error);
            })
        }
    }

    ngOnInit(): void {
        if (this.community?.id && this.user?.id && !this.user.is_staff && !this.user.is_superuser) {
            this.communitiesService.getUserMembership(this.community.id, this.user?.id).subscribe(r => {
                if (r.data.level.is_active) {
                    this.level = r.data.level;
                }
                this.accessLevel = r.data.access_level
                if (this.accessLevel == 'member') {
                    this.updateUserPoints();
                    if (this.largeAvatar) {
                        // If own users profile on sidebar
                        // Update the users points on socket call
                        this.subscription = this.socketService.getNewPoints().subscribe(data => {
                            this.updateUserPoints();
                        });

                        this.badgeSubscription = this.socketService.getNewBadge().subscribe(data => {
                            this.badgeProgressComponent?.getBadges();
                        });

                        this.commentSubscription = this.socketService.getNewComments().subscribe(data => {
                            this.getSecondaryStats()
                        });

                        this.pollVotesSubscription = this.socketService.getNewVotes().subscribe(data => {
                            this.getSecondaryStats()
                        });
                    }
                }
            }, e => {
                console.error(e)
            })
        }
    }

    updateUserPoints(): void {
        combineLatest([
            this.communitiesService.getCurrentDashboardCommunity().pipe(startWith(this.lastSelectedCommunity)),
            this.changeDate$.pipe(startWith({startDate: this.startDate, endDate: this.endDate}))
        ]).pipe(
            map(([community, trigger]) => ({community, trigger})),
            distinctUntilChanged((prev, cur) => {
                return prev.community?.id === cur.community?.id && prev.trigger?.startDate === cur.trigger?.startDate && prev.trigger?.endDate === cur.trigger?.endDate
            }),
            takeWhile(() => !this.user?.points_in_community),
            takeUntilDestroyed(this.destroyRef),
            switchMap(res => {
                this.userPoints = 0;
                if (!res.community) return EMPTY;
                this.getSecondaryStats()
                this.cdr.detectChanges();
                return this.rewardsService.getUserPoints(res.community.id, this.user.id, this.startDate, this.endDate)
            }),
        ).subscribe(res => {
            this.userPoints = res.points;
        });
    }


    ngOnDestroy(): void {
        this.changeDate$.next({});
        this.changeDate$.complete();
        this.subscription?.unsubscribe();
        this.badgeSubscription?.unsubscribe();
        this.communitySubscription?.unsubscribe();
        this.commentSubscription?.unsubscribe();
        this.pollVotesSubscription?.unsubscribe();
    }

    openLevels(): void {
        this.dialog.open(LevelsComponent, {
            panelClass: 'mediumWindow',
            data: {
                user: this.user,
                community: this.community
            }
        });
    }

    openBadges(): void {
        this.dialog.open(BadgesComponent, {panelClass: 'mediumWindow', maxHeight: '80vh'});
    }

    getPercent(value: number, requiredPoints: number): number {
        return (value * 100) / (requiredPoints || 1);
    }

    getUserPoints(event: 'allTime' | 'lastYear' | 'month'): void {
        this.pointsDate = event;
        const currentDate: Date = new Date();
        switch (event) {
            case "lastYear":
                this.endDate = this.formatDateToYYYYMMDD(new Date());
                const previousYearDate: Date = new Date(currentDate);
                previousYearDate.setFullYear(currentDate.getFullYear() - 1);
                this.startDate = this.formatDateToYYYYMMDD(endOfDay(previousYearDate)!);
                break;
            case "month":
                this.startDate = this.formatDateToYYYYMMDD(startOfMonth(new Date())!);
                this.endDate = this.formatDateToYYYYMMDD(endOfMonth(new Date())!);
                break;
            case "allTime":
                this.endDate = undefined;
                this.startDate = undefined;
                break;
        }
        this.changeDate$.next({startDate: this.startDate, endDate: this.endDate});
    }

    getSecondaryStats(): void {
        if (this.user.id) {
            this.communitiesService.userStats(this.lastSelectedCommunity?.id || 0, this.user.id, this.endDate, this.startDate).subscribe(r => {
                this.comments = r.comments;
                this.likes = r.likes;
                this.votes = r.votes;
                this.cdr.detectChanges();
            }, e => {
                console.error(e)
            })
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    private formatDateToYYYYMMDD(date: Date) {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11, so add 1
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }
}
